import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {WebmailApi, WebmailPortalControllerGetQuotaFolderEnum,} from "components/src/api/portal/generated";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {Utils} from "components/src/utils/Utils";
import {useWebmailReloadContext} from "enterprise-components/src/context/WebmailReloadContext";
import {useEffect, useState} from "react";
import ProgressBar from "react-bootstrap/ProgressBar";
import {useNavigate} from "react-router-dom";

const webmailApi = new WebmailApi(ApiConfig.config());

/**
 * Displays the mailbox quota for the Inbox as a progress bar.
 * Automatically hidden when the sidebar is in collapsed (icon-only) mode.
 * Renders nothing if the quota cannot be retrieved.
 */
export const WebmailQuota = () => {
    const navigate = useNavigate();
    const { t } = useTranslationWithOverride();
    const { reloadCounter } = useWebmailReloadContext();
    const [quotaUsage, setQuotaUsage] = useState<number>();
    const [quotaLimit, setQuotaLimit] = useState<number>();

    useEffect(() => {
        webmailApi
            .webmailPortalControllerGetQuota(
                WebmailPortalControllerGetQuotaFolderEnum.Inbox,
            )
            .then((response) => {
                const resource = response.data[0]?.resource[0];

                if (resource && resource.limit > 0) {
                    setQuotaUsage(resource.usage * 1024);
                    setQuotaLimit(resource.limit * 1024);
                }
            })
            .catch((err: Error | AxiosError) => {
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            });
    }, [navigate, reloadCounter]);

    if (quotaUsage === undefined || quotaLimit === undefined) {
        return null;
    }

    const percentage = Math.min(
        100,
        Math.round((quotaUsage / quotaLimit) * 100),
    );
    const variant =
        percentage >= 95 ? "danger" : percentage >= 80 ? "warning" : "success";
    // a mailbox can be over its quota, and there is no negative amount of free space. formatBytes derives
    // the unit with a logarithm, which of a negative number is NaN
    const remaining = Utils.formatBytes(Math.max(0, quotaLimit - quotaUsage));

    return (
        <div className="px-3 pb-3">
            <div className="sidebar-storage-card">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="sidebar-storage-label fw-semibold">
                        {t("portal.messages.disk-usage")}
                    </small>
                    <small className="sidebar-storage-label">
                        {Utils.formatBytes(quotaUsage)}/
                        {Utils.formatBytes(quotaLimit)}
                    </small>
                </div>
                <ProgressBar
                    now={percentage}
                    variant={variant}
                    className="cm-quota-progress-bar mb-2"
                />
                <small className="sidebar-storage-label">
                    {percentage}%{" "}
                    {t("portal-dashboard-quota-used")},{" "}
                    {remaining}{" "}
                    {t("portal-dashboard-quota-free")}
                </small>
            </div>
        </div>
    );
};
